import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Restaurant as RestaurantIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Menu {
  _id: string;
  date: string;
  kindergarten: {
    _id: string;
    name: string;
  };
  breakfast: {
    items: Array<{
      name: string;
      description?: string;
      calories?: number;
      allergens?: string[];
    }>;
    totalCalories: number;
  };
  fruitPlate?: {
    items: Array<{
      name: string;
      description?: string;
      calories?: number;
      allergens?: string[];
    }>;
    totalCalories: number;
  };
  lunch: {
    items: Array<{
      name: string;
      description?: string;
      calories?: number;
      allergens?: string[];
    }>;
    totalCalories: number;
  };
  dinner: {
    items: Array<{
      name: string;
      description?: string;
      calories?: number;
      allergens?: string[];
    }>;
    totalCalories: number;
  };
  afternoonSnack?: {
    items: Array<{
      name: string;
      description?: string;
      calories?: number;
      allergens?: string[];
    }>;
    totalCalories: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface Kindergarten {
  _id: string;
  name: string;
}

interface MenuFormData {
  date: string;
  kindergartenId: string;
  kindergartenIds?: string[];
  breakfast: string;
  fruitPlate: string;
  lunch: string;
  dinner: string;
  afternoonSnack: string;
}

const Menus: React.FC = () => {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [viewingMenu, setViewingMenu] = useState<Menu | null>(null);
  const [formData, setFormData] = useState<MenuFormData>({
    date: '',
    kindergartenId: '',
    kindergartenIds: [],
    breakfast: '',
    fruitPlate: '',
    lunch: '',
    dinner: '',
    afternoonSnack: '',
  });

  const { hasPermission } = useAdminAuth();

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchKindergartens();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchKindergartens = async () => {
    try {
      const response = await api.get('/kindergartens');
      setKindergartens(response.data.data || []);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження дитячих садків');
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const menusRes = await api.get('/menus', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
        },
      });
      
      setMenus(menusRes.data.data || []);
      setTotalItems(menusRes.data.pagination?.totalItems || 0);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (menu?: Menu) => {
    if (menu) {
      setEditingMenu(menu);
      
      // Отримуємо назви блюд з об'єктів
      const breakfastText = menu.breakfast?.items?.[0]?.name || '';
      const lunchText = menu.lunch?.items?.[0]?.name || '';
      const dinnerText = menu.dinner?.items?.[0]?.name || '';
      const fruitPlateText = menu.fruitPlate?.items?.[0]?.name || '';
      const afternoonSnackText = menu.afternoonSnack?.items?.[0]?.name || '';
      
      setFormData({
        date: menu.date.split('T')[0], // Конвертуємо в формат YYYY-MM-DD
        kindergartenId: menu.kindergarten._id,
        kindergartenIds: [menu.kindergarten._id],
        breakfast: breakfastText,
        fruitPlate: fruitPlateText,
        lunch: lunchText,
        dinner: dinnerText,
        afternoonSnack: afternoonSnackText,
      });
    } else {
      setEditingMenu(null);
      setFormData({
        date: '',
        kindergartenId: '',
        kindergartenIds: [],
        breakfast: '',
        fruitPlate: '',
        lunch: '',
        dinner: '',
        afternoonSnack: '',
      });
    }
    setOpenDialog(true);
  };

  const handleKindergartenSelection = (selectedValues: string[]) => {
    const hasAll = selectedValues.includes('ALL');
    const currentIds = formData.kindergartenIds || [];
    
    if (hasAll) {
      // Якщо вибрано "Всі садки", очищаємо інші вибори
      setFormData(prev => ({ ...prev, kindergartenIds: ['ALL'] }));
    } else {
      // Якщо вибрано конкретні садки, видаляємо "ALL" якщо він був
      const filteredValues = selectedValues.filter(id => id !== 'ALL');
      setFormData(prev => ({ ...prev, kindergartenIds: filteredValues }));
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMenu(null);
    setFormData({
      date: '',
      kindergartenId: '',
      kindergartenIds: [],
      breakfast: '',
      fruitPlate: '',
      lunch: '',
      dinner: '',
      afternoonSnack: '',
    });
  };

  const handleOpenViewDialog = (menu: Menu) => {
    setViewingMenu(menu);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingMenu(null);
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      
      // Передаємо дату як календарний день YYYY-MM-DD без UTC-конверсії
      formDataToSend.append('date', formData.date);
      
      if (!editingMenu) {
        // Визначаємо, куди застосувати: всі садки або вибрані (тільки для створення)
        const ids = formData.kindergartenIds || [];
        const applyToAll = ids.includes('ALL');
        if (applyToAll) {
          formDataToSend.append('applyToAll', 'true');
        } else if (ids.length > 0) {
          // Фільтруємо 'ALL' з масиву, якщо він там є
          const filteredIds = ids.filter(id => id !== 'ALL');
          if (filteredIds.length > 0) {
            formDataToSend.append('kindergartenIds', JSON.stringify(filteredIds));
          }
        } else if (formData.kindergartenId) {
          formDataToSend.append('kindergarten', formData.kindergartenId);
        }
      }
      
      // Створюємо правильну структуру для блюд
      const breakfastData = {
        items: formData.breakfast ? [{ name: formData.breakfast, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('breakfast', JSON.stringify(breakfastData));
      const fruitPlateData = {
        items: formData.fruitPlate ? [{ name: formData.fruitPlate, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('fruitPlate', JSON.stringify(fruitPlateData));
      
      const lunchData = {
        items: formData.lunch ? [{ name: formData.lunch, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('lunch', JSON.stringify(lunchData));
      
      const dinnerData = {
        items: formData.dinner ? [{ name: formData.dinner, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('dinner', JSON.stringify(dinnerData));
      const afternoonSnackData = {
        items: formData.afternoonSnack ? [{ name: formData.afternoonSnack, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('afternoonSnack', JSON.stringify(afternoonSnackData));

      if (editingMenu) {
        await api.put(`/menus/${editingMenu._id}`, formDataToSend);
      } else {
        await api.post('/menus', formDataToSend);
      }

      handleCloseDialog();
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка збереження');
    }
  };

  const handleApplyToAll = async () => {
    if (!editingMenu) return;
    try {
      const formDataToSend = new FormData();

      // Створюємо правильну структуру для блюд (дата не потрібна для масового оновлення)
      const breakfastData = {
        items: formData.breakfast ? [{ name: formData.breakfast, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('breakfast', JSON.stringify(breakfastData));

      const fruitPlateData = {
        items: formData.fruitPlate ? [{ name: formData.fruitPlate, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('fruitPlate', JSON.stringify(fruitPlateData));

      const lunchData = {
        items: formData.lunch ? [{ name: formData.lunch, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('lunch', JSON.stringify(lunchData));

      const dinnerData = {
        items: formData.dinner ? [{ name: formData.dinner, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('dinner', JSON.stringify(dinnerData));

      const afternoonSnackData = {
        items: formData.afternoonSnack ? [{ name: formData.afternoonSnack, calories: 0 }] : [],
        totalCalories: 0
      };
      formDataToSend.append('afternoonSnack', JSON.stringify(afternoonSnackData));

      await api.put(`/menus/${editingMenu._id}/apply-to-all`, formDataToSend);
      handleCloseDialog();
      fetchData();
      window.alert('Зміни застосовано до всіх меню на цю дату.');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка масового оновлення');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити це меню?')) {
      try {
        await api.delete(`/menus/${id}`);
        fetchData();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getMealIcon = (mealType: string) => {
    switch (mealType) {
      case 'breakfast':
        return '🌅';
      case 'fruitPlate':
        return '🍎';
      case 'lunch':
        return '🌞';
      case 'dinner':
        return '🌙';
      case 'afternoonSnack':
        return '🥪';
      default:
        return '🍽️';
    }
  };

  const getMealText = (mealData: any) => {
    try {
      if (!mealData) return '';
      
      // Якщо це об'єкт (новий формат)
      if (typeof mealData === 'object') {
        return mealData.items?.[0]?.name || '';
      }
      
      // Якщо це рядок (старий формат)
      if (typeof mealData === 'string') {
        const data = JSON.parse(mealData);
        return data.items?.[0]?.name || '';
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Меню
        </Typography>
        {hasPermission('menus', 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Додати меню
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Дитячий садок</TableCell>
                  <TableCell>Сніданок</TableCell>
                  <TableCell>Фруктова тарілка</TableCell>
                  <TableCell>Обід</TableCell>
                  <TableCell>Вечеря</TableCell>
                  <TableCell>Підвечірок</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menus.map((menu) => (
                  <TableRow key={menu._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        {formatDate(menu.date)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        {menu.kindergarten.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getMealIcon('breakfast')}</span>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {getMealText(menu.breakfast)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getMealIcon('fruitPlate')}</span>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {getMealText(menu.fruitPlate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getMealIcon('lunch')}</span>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {getMealText(menu.lunch)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getMealIcon('dinner')}</span>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {getMealText(menu.dinner)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getMealIcon('afternoonSnack')}</span>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {getMealText(menu.afternoonSnack)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenViewDialog(menu)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('menus', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(menu)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('menus', 'delete') && (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(menu._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Рядків на сторінці:"
            />
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMenu ? 'Редагувати меню' : 'Додати меню'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Дата"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Дитячий садок(и)</InputLabel>
              <Select
                multiple
                value={formData.kindergartenIds || []}
                onChange={(e) => handleKindergartenSelection(e.target.value as string[])}
                renderValue={(selected) => {
                  if ((selected as string[]).includes('ALL')) return 'Всі садки';
                  const names = (selected as string[]).map(id => kindergartens.find(k => k._id === id)?.name || id);
                  return names.join(', ');
                }}
                label="Дитячий садок(и)"
              >
                <MenuItem value="ALL">
                  <Checkbox checked={(formData.kindergartenIds || []).includes('ALL')} />
                  <ListItemText primary="Всі садки" />
                </MenuItem>
                {kindergartens.map((kindergarten) => (
                  <MenuItem key={kindergarten._id} value={kindergarten._id}>
                    <Checkbox checked={(formData.kindergartenIds || []).includes(kindergarten._id)} />
                    <ListItemText primary={kindergarten.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Меню на день
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span>{getMealIcon('breakfast')}</span>
                  <Typography variant="subtitle1">Сніданок</Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Сніданок"
                  value={formData.breakfast}
                  onChange={(e) => setFormData(prev => ({ ...prev, breakfast: e.target.value }))}
                  multiline
                  rows={3}
                  required
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span>{getMealIcon('fruitPlate')}</span>
                  <Typography variant="subtitle1">Фруктова тарілка</Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Фруктова тарілка"
                  value={formData.fruitPlate}
                  onChange={(e) => setFormData(prev => ({ ...prev, fruitPlate: e.target.value }))}
                  multiline
                  rows={3}
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span>{getMealIcon('lunch')}</span>
                  <Typography variant="subtitle1">Обід</Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Обід"
                  value={formData.lunch}
                  onChange={(e) => setFormData(prev => ({ ...prev, lunch: e.target.value }))}
                  multiline
                  rows={3}
                  required
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span>{getMealIcon('dinner')}</span>
                  <Typography variant="subtitle1">Вечеря</Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Вечеря"
                  value={formData.dinner}
                  onChange={(e) => setFormData(prev => ({ ...prev, dinner: e.target.value }))}
                  multiline
                  rows={3}
                  required
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span>{getMealIcon('afternoonSnack')}</span>
                  <Typography variant="subtitle1">Підвечірок</Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Підвечірок"
                  value={formData.afternoonSnack}
                  onChange={(e) => setFormData(prev => ({ ...prev, afternoonSnack: e.target.value }))}
                  multiline
                  rows={3}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          {editingMenu && hasPermission('menus', 'update') && (
            <Button onClick={handleApplyToAll} variant="outlined">
              Застосувати до всіх
            </Button>
          )}
          <Button onClick={handleSubmit} variant="contained">
            {editingMenu ? 'Зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог перегляду меню */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Меню на {viewingMenu && formatDate(viewingMenu.date)}
        </DialogTitle>
        <DialogContent>
          {viewingMenu && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Дитячі садки
                </Typography>
                <Typography variant="body1">
                  {viewingMenu.kindergarten ? viewingMenu.kindergarten.name : 'Всі садки'}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {viewingMenu.breakfast && viewingMenu.breakfast.items && viewingMenu.breakfast.items.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <span>{getMealIcon('breakfast')}</span>
                      <Typography variant="h6">Сніданок</Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {viewingMenu.breakfast.items.map((item, index) => (
                        <Typography key={index} variant="body1">
                          {item.name}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {viewingMenu.fruitPlate && viewingMenu.fruitPlate.items && viewingMenu.fruitPlate.items.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <span>{getMealIcon('fruitPlate')}</span>
                      <Typography variant="h6">Фруктова тарілка</Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {viewingMenu.fruitPlate.items.map((item, index) => (
                        <Typography key={index} variant="body1">
                          {item.name}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {viewingMenu.lunch && viewingMenu.lunch.items && viewingMenu.lunch.items.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <span>{getMealIcon('lunch')}</span>
                      <Typography variant="h6">Обід</Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {viewingMenu.lunch.items.map((item, index) => (
                        <Typography key={index} variant="body1">
                          {item.name}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {viewingMenu.dinner && viewingMenu.dinner.items && viewingMenu.dinner.items.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <span>{getMealIcon('dinner')}</span>
                      <Typography variant="h6">Вечеря</Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {viewingMenu.dinner.items.map((item, index) => (
                        <Typography key={index} variant="body1">
                          {item.name}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {viewingMenu.afternoonSnack && viewingMenu.afternoonSnack.items && viewingMenu.afternoonSnack.items.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <span>{getMealIcon('afternoonSnack')}</span>
                      <Typography variant="h6">Підвечірок</Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {viewingMenu.afternoonSnack.items.map((item, index) => (
                        <Typography key={index} variant="body1">
                          {item.name}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Додаткова інформація
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата створення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingMenu.createdAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Останнє оновлення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingMenu.updatedAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Menus; 